<style lang="scss">
  @import 'scss/main';
</style>
<template>

  <!-- People Profiles -->
  <div class="people-profiles">

    <!-- Threshold Profiles -->
    <div class="people-profiles__grid">
      <Profile v-for="(profile, profileIndex) in thresholdProfiles" :key="profileIndex" :profile="profile" />
    </div>

    <!-- Overflow Profiles -->
    <div v-if="hasCollapse" id="overflow-profiles" class="collapse">
      <div class="collapse__content">
        <div class="people-profiles__grid people-profiles__grid--overflow">
          <Profile v-for="(profile, profileIndex) in overflowProfiles" :key="profileIndex" :profile="profile" />
        </div>
      </div>
    </div>

    <!-- Profile Controls -->
    <div v-show="hasCollapse" class="people-profiles__controls" ref="testing">
      <button @click="toggleCollapse" aria-expanded="false" aria-controls="overflow-profiles" data-toggle="true" :class="['button', 'bg-white', collapseButtonAttributes.icon]">{{ collapseButtonAttributes.text }}</button>
    </div>
  </div>
</template>
<script>
  import * as Sentry from '@sentry/browser'
  import algoliasearch from 'algoliasearch'
  import Profile from './components/Profile.vue'
  import MissingProfilesError from './helpers/missingProfiles'

  export default {
    name: 'App',

    props: {
      requestedProfiles: String
    },

    components: {
      Profile
    },

    data() {
      return {
        algoliaClient: null,
        algoliaIndex: null,
        collapseButtonAttributes: {
          icon: 'icon-plus',
          text: 'Show more'
        },
        collapseThreshold: 4,
        emailRegex: /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)(\[(\d)\])?/,
        inputEmails: {},
        inputManuals: [],
        profiles: [],
      }
    },

    computed: {

      /*
       * Returns the current window location pathname
       *
       * @returns {string}
       */
      searchUrl() {
        return window.location.pathname
      },

      /*
       * Returns a sanitised array derived from the input
       *
       * @returns {Array}
       */
      sanitisedInput() {

        // Assign requested profiles to local variable
        let sanitisedInput = this.requestedProfiles

        // Strip any line breaks or commas
        sanitisedInput = sanitisedInput.replace(/\\n/gm, ' ')
        
        // Convert all multiple spaces, line breaks etc into a single space
        sanitisedInput = sanitisedInput.replace(/\s\s+/gm, ' ')
        
        // Trim the email addresses
        sanitisedInput = sanitisedInput.trim()

        // Split the email addresses into an array
        sanitisedInput = sanitisedInput.split(';')

        // Return sanitised input
        return sanitisedInput
      },

      /*
       * Returns an array of email addresses
       *
       * @returns {Array}
       */
      emailAddresses() {
        return Object.keys(this.inputEmails)
      },

      /*
       * Returns sanitised array of email addresses for use in the search payload
       *
       * @returns {Array}
       */
      emailPayload() {
        return this.emailAddresses.map((email) => {
          return `email:${email}`
        }).join(' OR ')
      },

      /*
       * Returns whether or not the people profiles should collapse
       *
       * @returns {boolean}
       */
      hasCollapse() {
        return this.profiles.length > this.collapseThreshold
      },

      /*
       * Returns either the full array of profiles, or a trimmed subsection depending on whether the profiles are collapsed or expanded
       *
       * @returns {boolean}
       */
      thresholdProfiles() {
        return this.profiles.slice(0, this.collapseThreshold)
      },

      /*
       * Returns either the full array of profiles, or a trimmed subsection depending on whether the profiles are collapsed or expanded
       *
       * @returns {boolean}
       */
      overflowProfiles() {
        return this.profiles.slice(this.collapseThreshold)
      },
    },

    methods: {
      getJSON(json) {
        try {
          return JSON.parse(json)
        } catch (e) {
          return false
        }
      },

      getEmail(email) {
        return email.match(this.emailRegex)
      },

      getInputs() {
        this.sanitisedInput.forEach((profile) => {

          const json = this.getJSON(profile)
          const email = this.getEmail(profile)

          if (json) {
            this.inputManuals.push(json)
          } else if (email) {
            this.inputEmails[email[1]] = (email[3] ? email[3]: 0)
          }
        })
      },

      toggleCollapse(event) {
        const collapsed = (event.target.getAttribute('aria-expanded') === 'true')
        this.collapseButtonAttributes = collapsed ? {
          icon: 'icon-plus',
          text: 'Show more'
        }: {
          icon: 'icon-minus',
          text: 'Show less'
        }
      },

      reportMissingProfiles(algoliaProfiles) {
        
        // Initialise empty array for storing missing profiles
        let missingProfiles = []

        // Loop each profile to determine if it was found in Algolia
        this.emailAddresses.forEach((emailAddress) => {

          // Attempt to retrieve matching algolia profile
          const algoliaProfile = algoliaProfiles.find(algoliaProfile => algoliaProfile.email == emailAddress)

          // If the profile was not found, push it to the missing profiles array
          if (!algoliaProfile) {
            missingProfiles.push(emailAddress)
          }
        })

        // Determine if there were any missing profiles
        if (missingProfiles.length > 0) {

          // Report missing profiles to Sentry
          Sentry.withScope(function(scope) {

            // This is not a "real" error, set the error level as "info"
            scope.setLevel('info')

            // Push document title to Sentry
            scope.setTag('document.title', document.title);

            // Capture exception
            Sentry.captureException(new MissingProfilesError(missingProfiles))
          })
        }
      }
    },

    mounted() {

      // Get the provided inputs (people profiles and manual profiles)
      this.getInputs()

      // Initialise Algolia Client
      this.algoliaClient = algoliasearch('RFW6CI5PBU', '6a01e944e48e15956dcf150ab4c06cbd')

      // Initialise Algolia Index
      this.algoliaIndex = this.algoliaClient.initIndex('prod_PROFILES')

      // Verify that an email payload exists
      if (this.emailPayload) {

        // Search Algolia for the email addresses.
        // hitsPerPage set to 1000 as no option for unlimited
        this.algoliaIndex.search('', {
          filters: this.emailPayload,
          hitsPerPage: 1000
        }).then(({ hits }) => {

          // Update people profiles with the hits
          const peopleProfiles = hits.map((profile) => {
            return {
              email: profile.email,
              forename: profile.firstName,
              organisation: profile.positions.internal.organisation,
              profile_photo: profile.profilePicture,
              profile_url: profile.profileUrl,
              role: profile.positions.internal.role,
              surname: profile.lastName,
              title: profile.title == null ? '' : profile.title,
            }
          })

          // Report missing profiles
          this.reportMissingProfiles(peopleProfiles)

          // Set profiles to combine both people profiles and manual profiles
          this.profiles = [...peopleProfiles, ...this.inputManuals]
        })
      } else {

        // Set profiles to manual profiles only
        this.profiles = this.inputManuals
      }
    }
  }
</script>